import React, { useState, useEffect } from 'react';
import {
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { format } from 'date-fns';
import { API_BASE_URL, API_ENDPOINTS } from '../constants';
import { storageService } from '../services';

interface WeightRecord {
  id: string;
  date: string;
  weight: number;
}

interface FeedingRecord {
  id: string;
  date: string;
  cattleType: string;
  roughage: { weight: number; dryMatter: number };
  concentrate: { weight: number; dryMatter: number };
  tmr: { weight: number; dryMatter: number };
}

interface FCRDataPoint {
  date: string;
  fcr: number;
  feedIntake: number;
  weightGain: number;
}

interface FCRChartProps {
  cowId: string;
  cowCattleType: string;
  startDate?: Date;
  endDate?: Date;
}

const FCRChart: React.FC<FCRChartProps> = ({
  cowId,
  cowCattleType,
  startDate,
  endDate,
}) => {
  const [data, setData] = useState<FCRDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [avgFCR, setAvgFCR] = useState<number>(0);
  const [totalFeedIntake, setTotalFeedIntake] = useState<number>(0);
  const [totalWeightGain, setTotalWeightGain] = useState<number>(0);

  useEffect(() => {
    fetchData();
  }, [cowId, startDate, endDate]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = storageService.getAuthToken();

      // Fetch weight records
      const weightResponse = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.HEALTH.WEIGHT_BY_COW(cowId)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!weightResponse.ok) {
        throw new Error('Failed to fetch weight records');
      }

      const weightRecords: WeightRecord[] = await weightResponse.json();

      // Fetch feeding records (using dummy data for now as we need to create API)
      // In real implementation, this should fetch from feeding records API filtered by cattle type
      const feedingData = await fetchFeedingData();

      // Filter by date range if provided
      let filteredWeights = weightRecords;
      let filteredFeeding = feedingData;

      if (startDate && endDate) {
        filteredWeights = weightRecords.filter((record) => {
          const recordDate = new Date(record.date);
          return recordDate >= startDate && recordDate <= endDate;
        });
        filteredFeeding = feedingData.filter((record) => {
          const recordDate = new Date(record.date);
          return recordDate >= startDate && recordDate <= endDate;
        });
      }

      // Sort by date
      filteredWeights.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      filteredFeeding.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );

      if (filteredWeights.length < 2) {
        setData([]);
        setAvgFCR(0);
        setError('ต้องมีข้อมูลน้ำหนักอย่างน้อย 2 ครั้งเพื่อคำนวณ FCR');
        return;
      }

      // Calculate FCR data
      const fcrData: FCRDataPoint[] = [];
      let cumulativeFeed = 0;
      const startWeight = filteredWeights[0].weight;

      // Create a map of feeding data by date
      const feedingMap = new Map<string, number>();
      filteredFeeding.forEach((record) => {
        const dateKey = record.date.split('T')[0];
        const totalDM =
          record.roughage.weight * (record.roughage.dryMatter / 100) +
          record.concentrate.weight * (record.concentrate.dryMatter / 100) +
          record.tmr.weight * (record.tmr.dryMatter / 100);
        feedingMap.set(dateKey, totalDM);
      });

      // Calculate cumulative feed intake and FCR for each weight record
      filteredWeights.forEach((weightRecord, index) => {
        if (index === 0) return; // Skip first record

        const currentDate = new Date(weightRecord.date);
        const previousDate = new Date(filteredWeights[index - 1].date);

        // Sum feeding between previous and current weight record
        let periodFeed = 0;
        for (
          let d = new Date(previousDate);
          d <= currentDate;
          d.setDate(d.getDate() + 1)
        ) {
          const dateKey = d.toISOString().split('T')[0];
          periodFeed += feedingMap.get(dateKey) || 0;
        }

        cumulativeFeed += periodFeed;
        const weightGain = weightRecord.weight - startWeight;
        const fcr = weightGain > 0 ? cumulativeFeed / weightGain : 0;

        fcrData.push({
          date: weightRecord.date,
          fcr: parseFloat(fcr.toFixed(2)),
          feedIntake: parseFloat(cumulativeFeed.toFixed(2)),
          weightGain: parseFloat(weightGain.toFixed(2)),
        });
      });

      // Calculate overall statistics
      if (fcrData.length > 0) {
        const lastData = fcrData[fcrData.length - 1];
        setAvgFCR(lastData.fcr);
        setTotalFeedIntake(lastData.feedIntake);
        setTotalWeightGain(lastData.weightGain);
      }

      setData(fcrData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchFeedingData = async (): Promise<FeedingRecord[]> => {
    // This is a placeholder - in real implementation, fetch from feeding API
    // For now, return empty array or fetch from local storage
    try {
      const feedingRecords = localStorage.getItem('feedingRecords');
      if (feedingRecords) {
        const allRecords: FeedingRecord[] = JSON.parse(feedingRecords);
        // Filter by cattle type
        return allRecords.filter((record) => record.cattleType === cowCattleType);
      }
    } catch (err) {
      console.error('Error fetching feeding data:', err);
    }
    return [];
  };

  const formatXAxis = (tickItem: string) => {
    try {
      return format(new Date(tickItem), 'dd/MM/yy');
    } catch {
      return tickItem;
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-gray-300 p-3 rounded shadow-lg">
          <p className="font-semibold text-sm mb-2">
            {format(new Date(label), 'dd MMM yyyy')}
          </p>
          {payload.map((entry: any, index: number) => {
            const value =
              typeof entry.value === 'number' ? entry.value : Number(entry.value) || 0;
            let unit = '';
            if (entry.dataKey === 'fcr') unit = '';
            else if (entry.dataKey === 'feedIntake') unit = ' กก.';
            else if (entry.dataKey === 'weightGain') unit = ' กก.';

            return (
              <p key={index} className="text-xs" style={{ color: entry.color }}>
                {entry.name}: {value.toFixed(2)}
                {unit}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-gray-500">
          ไม่มีข้อมูลเพียงพอในการคำนวณ FCR (ต้องมีข้อมูลน้ำหนักและอาหาร)
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-orange-700 mb-1">
            FCR (Feed Conversion Ratio)
          </h3>
          <p className="text-2xl font-bold text-orange-900">{avgFCR.toFixed(2)}</p>
          <p className="text-xs text-orange-600 mt-1">
            ยิ่งต่ำยิ่งดี (ใช้อาหารน้อย ได้น้ำหนักมาก)
          </p>
        </div>

        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-teal-700 mb-1">
            อาหารที่กินทั้งหมด (DM)
          </h3>
          <p className="text-2xl font-bold text-teal-900">
            {totalFeedIntake.toFixed(2)} <span className="text-lg">กก.</span>
          </p>
        </div>

        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-indigo-700 mb-1">
            น้ำหนักที่เพิ่มขึ้น
          </h3>
          <p className="text-2xl font-bold text-indigo-900">
            {totalWeightGain.toFixed(2)} <span className="text-lg">กก.</span>
          </p>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h4 className="font-semibold text-blue-900 mb-2">📊 ความหมายของ FCR:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• FCR = ปริมาณอาหารที่กิน (กก.) ÷ น้ำหนักที่เพิ่ม (กก.)</li>
          <li>• ยิ่ง FCR ต่ำ = ประสิทธิภาพการแปลงอาหารยิ่งดี</li>
          <li>
            • ตัวอย่าง: FCR = 6 หมายความว่า กินอาหาร 6 กก. ได้น้ำหนักเพิ่ม 1 กก.
          </li>
          <li>• โคขุนที่ดีควรมี FCR ประมาณ 5-7</li>
        </ul>
      </div>

      {/* Chart */}
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        กราฟ FCR ตามเวลา
      </h3>
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tickFormatter={formatXAxis} />
          <YAxis
            yAxisId="left"
            label={{ value: 'FCR', angle: -90, position: 'insideLeft' }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            label={{ value: 'น้ำหนัก/อาหาร (กก.)', angle: 90, position: 'insideRight' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="fcr"
            stroke="#f97316"
            strokeWidth={3}
            name="FCR"
            dot={{ r: 5 }}
          />
          <Bar
            yAxisId="right"
            dataKey="feedIntake"
            fill="#14b8a6"
            name="อาหารสะสม"
            opacity={0.6}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="weightGain"
            stroke="#6366f1"
            strokeWidth={2}
            name="น้ำหนักที่เพิ่ม"
            dot={{ r: 4 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default FCRChart;
